import { useState, useEffect } from 'react'
import { Grid } from '@mui/material'
import toast from 'react-hot-toast'
import dayjs from 'dayjs'
import CommonModal from './CommonModal'
import ButtonCustom from '../button/ButtonCustom'
import DateCustom from '../input/DateCustom'
import SelectCustom from '../select/SelectCustom'
import TextArea from '../textarea/TextArea'
import styles from './EditTimeOffModal.module.scss'

const LeaveDurationOptions = [
	{ label: 'Full Day', value: 'Full Day' },
	{ label: 'Half Day', value: 'Half Day' },
]

const TimeOffTypeOptions = [
	{ label: 'Sick Leave', value: 'Sick Leave' },
	{ label: 'Personal Leave', value: 'Personal Leave' },
	{ label: 'Bereavement', value: 'Bereavement' },
	{ label: 'Unpaid', value: 'Unpaid' },
]

const EditTimeOffModal = ({ 
	open, 
	onClose, 
	onSuccess, 
	requestData 
}) => {
	const [loading, setLoading] = useState(false)
	const [formData, setFormData] = useState({
		date: '',
		timeOffType: '',
		leaveDuration: '',
		reason: ''
	})

	// Populate form when requestData changes
	useEffect(() => {
		if (requestData && open) {
			setFormData({
				date: requestData.originalDate ? dayjs(requestData.originalDate) : (requestData.date ? dayjs(requestData.date) : ''),
				timeOffType: requestData.TimeOffType || '',
				reason: requestData.ReasonForTimeOff || '',
				leaveDuration: requestData.leaveDuration || '',
			})
		}
	}, [requestData, open])

	// Reset form when modal closes
	useEffect(() => {
		if (!open) {
			setFormData({
				date: '',
				timeOffType: '',
				reason: '',
				leaveDuration: '',
			})
		}
	}, [open])

	const handleSubmit = async () => {
		// Validation
		const isDateValid = formData.date && dayjs(formData.date).isValid()
		const isTypeValid = formData.timeOffType && formData.timeOffType.trim() !== ''
		const isReasonValid = formData.reason && formData.reason.trim() !== ''
		const isLeaveDurationValid = formData.leaveDuration && formData.leaveDuration.trim() !== ''
		
		if (!isDateValid || !isTypeValid || !isReasonValid || !isLeaveDurationValid) {
			toast.error('Please fill in all required fields')
			return
		}

		// Format the date for API submission
		const formattedDate = dayjs(formData.date).format('YYYY-MM-DD')

		try {
			setLoading(true)
			const response = await fetch(
				`${process.env.REACT_APP_REQUEST_URL}/api/v2/time-management/update-timeoff-request/${requestData.id}`,
				{
					method: 'PUT',
					credentials: 'include',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({
						date: formattedDate,
						timeOffType: formData.timeOffType,
						leaveDuration: formData.leaveDuration,
						reason: formData.reason
					})
				}
			)

			const result = await response.json()
			
			if (result.success) {
				toast.success('Time off request updated successfully!')
				onSuccess()
				onClose()
			} else {
				toast.error(result.message || 'Failed to update request')
			}
		} catch (error) {
			console.error('Error updating time off request:', error)
			toast.error('Failed to update request')
		} finally {
			setLoading(false)
		}
	}

	const handleCancel = () => {
		onClose()
	}

	return (
		<CommonModal
			open={open}
			handleClose={handleCancel}
			heading="Edit Time Off Request"
			className={styles.editTimeOffModal}
			footerContent={
				<div className={styles.footerBtns}>
					<ButtonCustom
						variant="outlined"
						size="small"
						onClick={handleCancel}
						style={{ minHeight: '40px' }}
						disabled={loading}
					>
						Cancel
					</ButtonCustom>
					<ButtonCustom
						variant="default"
						size="lg"
						onClick={handleSubmit}
						disabled={loading}
					>
						{loading ? 'Updating...' : 'Update Request'}
					</ButtonCustom>
				</div>
			}
		>
			<form className={styles.formWrp}>
				<Grid container spacing={1.5}>
					<Grid size={{ xs: 12, md: 12 }}>
						<div className={styles.formField}>
							<label htmlFor="date" className={styles.label}>
								Date *
							</label>
							<DateCustom
								name="date"
								setDataFunction={setFormData}
								data={formData}
								value={formData.date}
							/>
						</div>
					</Grid>

					<Grid size={{ xs: 12, sm: 12 }}>
						<div className={styles.formField}>
							<label
								htmlFor="TimeOffType"
								className={styles.label}
							>
								Time Off Type *
							</label>
							<SelectCustom
								label="Select"
								options={TimeOffTypeOptions}
								defaultValue={formData.timeOffType}
								setDataFunction={(selectedOption) => {
									setFormData(prev => ({ 
										...prev, 
										timeOffType: selectedOption ? selectedOption.value : '' 
									}))
								}}
							/>
						</div>
					</Grid>
					<Grid size={{ xs: 12, sm: 12 }}>
						<div className={styles.formField}>
							<label
								htmlFor="leaveDuration"
								className={styles.label}
							>
								Leave Duration *
							</label>
							<SelectCustom
								label="Select"
								options={LeaveDurationOptions}
								defaultValue={formData.leaveDuration}
								setDataFunction={(selectedOption) => {
									setFormData(prev => ({ 
										...prev, 
										leaveDuration: selectedOption ? selectedOption.value : '' 
									}))
								}}
							/>
						</div>
					</Grid>
					
					<Grid size={{ xs: 12, md: 12 }}>
						<div className={styles.formField}>
							<label
								htmlFor="reason"
								className={styles.label}
							>
								Reason for Time Off *
							</label>
							<TextArea
								name="reason"
								id="reason"
								placeholder="Write here"
								value={formData.reason}
								onChange={(e) => setFormData(prev => ({ 
									...prev, 
									reason: e.target.value 
								}))}
							/>
						</div>
					</Grid>
				</Grid>
			</form>
		</CommonModal>
	)
}

export default EditTimeOffModal
